// Prefer same-origin relative URLs when deployed behind K8s ingress.
// Cross-origin calls (frontend on kompa.cash → API on emergent.host)
// break when Cloudflare bot-management cookies can't round-trip.
const _configured = process.env.REACT_APP_BACKEND_URL || '';
const API_URL = (() => {
  if (typeof window === 'undefined') return _configured;
  try {
    const loc = window.location;
    // localhost / dev — honour the env var for cross-port calls
    if (loc.hostname === 'localhost' || loc.hostname === '127.0.0.1') return _configured;
    // Deployed: if current origin differs from configured backend,
    // use same-origin (the ingress routes /api/* to the backend).
    if (_configured) {
      const backend = new URL(_configured);
      if (backend.host !== loc.host) return '';
    }
    return _configured;
  } catch { return _configured; }
})();
const TOKEN_KEY = 'kompa_token';
const REFRESH_KEY = 'kompa_refresh';

// ── Safe localStorage wrapper ──────────────────────────────────────────
// Safari private browsing, older Android WebViews, and browsers with full
// storage quota throw SecurityError / QuotaExceededError on ANY
// localStorage call. Wrapping at module level prevents a WSOD on boot.
function _storageGet(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}
function _storageSet(key, val) {
  try { localStorage.setItem(key, val); } catch { /* silent */ }
}
function _storageRemove(key) {
  try { localStorage.removeItem(key); } catch { /* silent */ }
}

let authToken = _storageGet(TOKEN_KEY) || null;
let refreshToken = _storageGet(REFRESH_KEY) || null;

export function setTokens(access, refresh) {
  authToken = access;
  refreshToken = refresh;
  if (access) _storageSet(TOKEN_KEY, access);
  else _storageRemove(TOKEN_KEY);
  if (refresh) _storageSet(REFRESH_KEY, refresh);
  else _storageRemove(REFRESH_KEY);
}

export function clearTokens() {
  authToken = null;
  refreshToken = null;
  _storageRemove(TOKEN_KEY);
  _storageRemove(REFRESH_KEY);
}

export function getToken() {
  return authToken;
}

/**
 * Safely read JSON from a Response.
 * Platform scripts (emergent-main.js) may consume the body stream
 * before our code runs. clone() creates an independent copy.
 */
async function safeJson(res) {
  try {
    return await res.clone().json();
  } catch {
    // clone failed or body unreadable — try original as last resort
    return res.json();
  }
}

async function tryRefresh() {
  if (!refreshToken) return false;
  try {
    const res = await fetch(`${API_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!res.ok) return false;
    const data = await safeJson(res);
    setTokens(data.access_token, data.refresh_token);
    return true;
  } catch {
    return false;
  }
}

export default async function api(path, options = {}, _retried = false) {
  const headers = {
    'Content-Type': 'application/json',
    ...(authToken && { Authorization: `Bearer ${authToken}` }),
    ...options.headers,
  };

  let res;
  const url = `${API_URL}${path}`;
  // Auth endpoints must NEVER attempt a silent token refresh — a 401 here
  // means "bad credentials" (login) or "no refresh possible" (refresh).
  // Refreshing on the login response would also mask the real auth error.
  const isAuthEndpoint = /^\/api\/auth\//.test(path);
  try {
    res = await fetch(url, { ...options, headers });
  } catch (fetchErr) {
    // Retry once on transient network failures (Cloudflare resets, DNS hiccups)
    if (!_retried) {
      try {
        await new Promise(r => setTimeout(r, 1200));
        res = await fetch(url, { ...options, headers });
      } catch {
        throw new Error('Sin conexion al servidor. Verifica tu internet e intenta de nuevo.');
      }
    } else {
      throw new Error('Sin conexion al servidor. Verifica tu internet e intenta de nuevo.');
    }
  }

  // If 401 and we haven't retried yet, attempt token refresh (skipped on
  // /api/auth/* so login failures aren't masked by a refresh loop).
  if (res.status === 401 && !_retried && refreshToken && !isAuthEndpoint) {
    const refreshed = await tryRefresh();
    if (refreshed) return api(path, options, true);
  }

  if (!res.ok) {
    // Try to surface the backend's detail message first — the KYC gate
    // now returns a step-specific reason (e.g. "debes subir tu cédula
    // de identidad desde tu perfil"), and the user benefits from seeing
    // it. Falls back to the generic verification hint only when the
    // backend didn't supply a usable detail.
    const isPaymentPath = /^\/api\/(payments|caurapay)(\/|$)/.test(path);
    const isLoginPath = path === '/api/auth/login';
    let backendDetail = '';
    let backendCode = '';
    let parsedBody = null;
    try {
      const bodyText = await res.clone().text();
      if (bodyText && bodyText.trim()) {
        try {
          parsedBody = JSON.parse(bodyText.trim());
          const d = parsedBody && parsedBody.detail;
          if (typeof d === 'string') {
            backendDetail = d;
          } else if (d && typeof d === 'object' && !Array.isArray(d)) {
            // Structured detail (e.g. {code, message}) — surface the
            // human message and propagate the code to callers.
            backendDetail = d.message || '';
            backendCode = d.code || '';
          }
        } catch { /* not JSON */ }
      }
    } catch { /* ignore */ }

    if (res.status === 403 && isPaymentPath) {
      throw new Error(backendDetail || 'Usted debe verificar su cuenta a través de su perfil');
    }

    // Path-aware fallbacks: a 401 from the LOGIN endpoint is "wrong
    // credentials" — never "session expired" (the user has no session
    // yet). Mislabelling this is what caused users to suspect outages.
    let message = backendDetail || '';
    if (!message) {
      if (isLoginPath) {
        if (res.status === 401 || res.status === 400) message = 'Correo o contraseña incorrectos';
        else if (res.status === 429) message = 'Demasiados intentos. Espera un minuto e intenta de nuevo';
        else if (res.status >= 500) message = 'No pudimos conectar con el servidor. Intenta nuevamente en unos segundos.';
        else message = 'No pudimos iniciar sesión. Intenta nuevamente.';
      } else if (parsedBody && Array.isArray(parsedBody.detail) && parsedBody.detail.length > 0) {
        const raw = parsedBody.detail[0].msg || '';
        message = raw.replace('Value error, ', '') || 'Datos invalidos';
      } else {
        if (res.status === 400) message = 'Datos invalidos. Revisa el formulario';
        else if (res.status === 401) message = 'Sesion expirada. Inicia sesion nuevamente';
        else if (res.status === 403) message = 'No tiene permiso para esta accion';
        else if (res.status === 404) message = 'Recurso no encontrado';
        else if (res.status === 422) message = 'Datos invalidos. Revisa el formulario';
        else if (res.status === 429) message = 'Demasiados intentos. Espera un momento';
        else if (res.status >= 500) message = 'Error del servidor. Intenta mas tarde';
        else message = 'Error del servidor';
      }
    }
    const err = new Error(message);
    if (backendCode) err.code = backendCode;
    throw err;
  }

  return safeJson(res);
}
